// Environment-aware logger utility
// Replaces console.log statements with smart logging that respects production vs development

import { getCurrentTimestamp } from "./dateUtils";

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// Log levels
const LOG_LEVELS = {
  ERROR: 0,   // Always log (production + dev)
  WARN: 1,    // Production + dev
  INFO: 2,    // Dev only
  DEBUG: 3    // Dev only (verbose)
};

// Current log level based on environment
const CURRENT_LEVEL = isProduction ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;

class Logger {
  constructor(context = '') {
    this.context = context;
  }

  // Helper to format messages with context
  _format(level, ...args) {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0]; // HH:MM:SS
    const prefix = this.context ? `[${this.context}]` : '';
    return [`${timestamp} ${level} ${prefix}`, ...args];
  }

  // Helper to sanitize sensitive data
  _sanitize(data) {
    if (typeof data !== 'object' || data === null) return data;

    const sanitized = Array.isArray(data) ? [...data] : { ...data };
    const sensitiveFields = [
      'password', 'token', 'accessToken', 'refreshToken',
      'secret', 'apiKey', 'ssn', 'creditCard', 'cvv',
      'phone', 'email', 'address', 'signingToken'
    ];

    const sanitizeObject = (obj) => {
      if (typeof obj !== 'object' || obj === null) return obj;

      const result = Array.isArray(obj) ? [...obj] : { ...obj };

      Object.keys(result).forEach(key => {
        const lowerKey = key.toLowerCase();
        const shouldRedact = sensitiveFields.some(field =>
          lowerKey.includes(field.toLowerCase())
        );

        if (shouldRedact) {
          result[key] = '[REDACTED]';
        } else if (typeof result[key] === 'object' && result[key] !== null) {
          result[key] = sanitizeObject(result[key]);
        }
      });

      return result;
    };

    return sanitizeObject(sanitized);
  }

  // ERROR: Always logged (production + development)
  error(...args) {
    if (CURRENT_LEVEL >= LOG_LEVELS.ERROR) {
      console.error(...this._format('❌ ERROR', ...args));
    }
  }

  // WARN: Logged in production + development
  warn(...args) {
    if (CURRENT_LEVEL >= LOG_LEVELS.WARN) {
      console.warn(...this._format('⚠️  WARN', ...args));
    }
  }

  // INFO: Development only
  info(...args) {
    if (CURRENT_LEVEL >= LOG_LEVELS.INFO) {
      console.log(...this._format('ℹ️  INFO', ...args));
    }
  }

  // DEBUG: Development only (most verbose)
  debug(...args) {
    if (CURRENT_LEVEL >= LOG_LEVELS.DEBUG) {
      console.log(...this._format('🔍 DEBUG', ...args));
    }
  }

  // Special: Log with automatic data sanitization (removes PII/tokens)
  debugSafe(message, data) {
    if (CURRENT_LEVEL >= LOG_LEVELS.DEBUG) {
      const sanitized = this._sanitize(data);
      console.log(...this._format('🔍 DEBUG', message, sanitized));
    }
  }

  // Special: Log sensitive data ONLY in development
  devOnly(...args) {
    if (isDevelopment) {
      console.log(...this._format('🔧 DEV', ...args));
    }
  }

  // Special: Group logging (dev only)
  group(label, callback) {
    if (isDevelopment && CURRENT_LEVEL >= LOG_LEVELS.DEBUG) {
      console.group(label);
      try {
        callback();
      } finally {
        console.groupEnd();
      }
    }
  }

  // Special: Table logging (dev only)
  table(data) {
    if (isDevelopment && CURRENT_LEVEL >= LOG_LEVELS.DEBUG) {
      console.table(data);
    }
  }

  // Special: Performance timing
  time(label) {
    if (isDevelopment) {
      console.time(`⏱️  ${this.context ? `[${this.context}] ` : ''}${label}`);
    }
  }

  timeEnd(label) {
    if (isDevelopment) {
      console.timeEnd(`⏱️  ${this.context ? `[${this.context}] ` : ''}${label}`);
    }
  }
}

// Export factory function to create loggers with context
export const createLogger = (context) => new Logger(context);

// Export default instance for general use
export default new Logger();

// Export environment flags for conditional code
export const ENV = {
  isDevelopment,
  isProduction,
  isTest: process.env.NODE_ENV === 'test'
};
